import { Injectable, signal } from '@angular/core';

import { filterTagsByPathDepth } from '@features/kt/utils/filter-tags-by-path-depth.util';

import { tagsList } from '@api/sdk.gen';
import type { GetTagsItem } from '@api';

export type GetTagsOptions = {
  path?: string;
  depth?: number;
};

/**
 * Tag catalog gateway. The tag taxonomy is reference data, so the whole catalog is fetched once
 * per session with a single param-less `GET /v1/tags`, and every `path` / `depth` slice is cut
 * from that response client-side. Concurrent callers share the in-flight request.
 */
@Injectable({ providedIn: 'root' })
export class TagsApiService {
  #catalog: Promise<GetTagsItem[]> | null = null;
  readonly #catalogItems = signal<readonly GetTagsItem[]>([]);
  readonly catalogItems = this.#catalogItems.asReadonly();

  async getTags(options: GetTagsOptions = {}): Promise<GetTagsItem[]> {
    this.#catalog ??= this.#fetchCatalog();

    return filterTagsByPathDepth(await this.#catalog, options);
  }

  /** Drops the cached catalog so the next `getTags` call refetches it. */
  invalidateCatalog(): void {
    this.#catalog = null;
    this.#catalogItems.set([]);
  }

  async #fetchCatalog(): Promise<GetTagsItem[]> {
    try {
      const items = (await tagsList({
        throwOnError: true,
        responseStyle: 'data' as const,
      })) as unknown as GetTagsItem[];
      this.#catalogItems.set(items);
      return items;
    } catch (error) {
      // Let the next call (typically a retry from a failed field) start a fresh request.
      this.#catalog = null;
      throw error;
    }
  }
}
